import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { NavLink } from 'fluxible-router';
import shallowCompare from '../../lib/shallowCompare';
import { RequestStates } from '../../stores/PaginatedStore';
import Customer from '../../models/Customer';
import CouponCodeForm from './CouponCodeForm';
import ShoppingCart from '../../models/ShoppingCart';
import Store from '../../models/Store';
import ShoppingCartItem from '../ShoppingCartItem';
import SimilarProductsSection, { UnitTypes } from '../SimilarProductsSection';
import styles from './index.sass';

const LoadingSpinner = <span className="loading-spinner--white" />;

class ShoppingCartContents extends React.Component {
  static contextTypes = {
    executeAction: React.PropTypes.func,
    getUserAgent: React.PropTypes.func
  };

  static propTypes = {
    customer: React.PropTypes.instanceOf(Customer),
    store: React.PropTypes.instanceOf(Store),
    shoppingCart: React.PropTypes.instanceOf(ShoppingCart),
    shoppingCartError: React.PropTypes.object,
    shoppingCartRequestStatus: React.PropTypes.string
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      actionBarStuck: false
    };
    this.handleScroll = this._handleScroll.bind(this);
  }

  componentDidMount() {
    this._setActionBarOffset();
    this.handleScroll();
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  componentWillReceiveProps(nextProps) {
    if(this.props.shoppingCart && !this.props.shoppingCart.items.size && nextProps.shoppingCart && nextProps.shoppingCart.items.size) {
      this._setActionBarOffset();
    }
  }

  _setActionBarOffset() {
    if(this.refs.actionBar) {
      let elem = ReactDOM.findDOMNode(this.refs.actionBar);
      this.setState({actionBarOffsetTop: elem.offsetParent.offsetTop + elem.offsetTop});
    }
  }

  _handleScroll(e) {
    let bodyTop = document.body.scrollTop;
    let actionBarTop = this.state.actionBarOffsetTop;
    if(bodyTop > actionBarTop) {
      this.setState({actionBarStuck: true});
    } else {
      this.setState({actionBarStuck: false});
    }
  }

  renderItems(shoppingCart) {
    let customer = this.props.customer;
    return shoppingCart.items.map(item => {
      let itemProps = {
        key: item.id,
        item: item,
        storeId: shoppingCart.store.id,
        customer: customer,
        alert: shoppingCart.findAlertByItemId(item.id)
      };
      return <ShoppingCartItem {...itemProps} />;
    });
  }

  renderInvoice(invoice) {
    return (
      <div className={styles.invoice}>
        {invoice.map((invoiceItem, index) => {
          if (invoiceItem.name == 'Subtotal') {
            return null;
          }
          let className = `${styles.invoiceItem} ${styles.subtotalRow}`;
          if(invoiceItem.name === 'Total') {
            className += (' ' + styles.invoiceItemBold);
          }
          return (
            <div className={className} key={index}>
              <span className={styles.invoiceItemName} style={{color: invoiceItem.color}}>{invoiceItem.name}</span>
              <span className={styles.invoiceItemValue} style={{color: invoiceItem.color}}>{invoiceItem.value}</span>
            </div>
          );
        })}
      </div>
    );
  }

  renderAlerts(shoppingCart) {
    if(!shoppingCart) {return null;}
    return shoppingCart.alerts.map((alert, index) => {
      let props = {
        className: styles.alert,
        key: index
      };
      return <p {...props}>{alert.message}</p>;
    });
  }

  render() {
    let { shoppingCart, currentRoute, store, shoppingCartRequestStatus } = this.props;
    let loading = shoppingCartRequestStatus === RequestStates.Loading;
    let totalItemCount = shoppingCart ? shoppingCart.total_item_count : 0;

    if(!totalItemCount) {
      let startShoppingButtonProps = {
        className: styles.startShoppingButton,
        routeName: 'storeLandingPage'
      };
      return (
        <div className={`${styles.shoppingCart} ${styles.empty}`}>
          <p className={styles.header}>Your Bag is Empty</p>
          <p className={styles.emptyMessage}>You haven’t added any items to your bag yet.</p>
          <div className={styles.postTableActions}>
            <NavLink {...startShoppingButtonProps}>Start shopping</NavLink>
          </div>
        </div>
      );
    }

    let checkoutButtonProps = {
      className: styles.checkoutButton,
      disabled: loading || !shoppingCart || !shoppingCart.items.size,
      routeName: 'checkout'
    };
    let checkoutButton = <NavLink {...checkoutButtonProps}>{loading ? LoadingSpinner : 'Continue to shipping options'}</NavLink>;
    let mobileCheckoutButtonProps = _.extend({}, checkoutButtonProps, {className: `${styles.checkoutButton} ${styles.mobile}`});
    let mobileCheckoutButton = <NavLink {...mobileCheckoutButtonProps}>{loading ? LoadingSpinner : 'Checkout'}</NavLink>;
    let keepShoppingButtonProps = {
      className: styles.keepShoppingButton,
      routeName: 'storeLandingPage'
    };
    let keepShoppingButton = <NavLink {...keepShoppingButtonProps}>Keep shopping</NavLink>;
    let similarProductsSectionProps = {
      inventoryRecordId: shoppingCart.items.last().inventory_record.id,
      store: store,
      unitType: UnitTypes.Photo
    };

    let couponCodeFormProps = {
      shoppingCart: shoppingCart,
      store: store
    };
    let couponCodeForm = <CouponCodeForm {...couponCodeFormProps}/>;

    return (
      <div className={styles.shoppingCart}>
        <div className={styles.actionBarWrapper}>
          <div className={styles.actionBar + (this.state.actionBarStuck ? (' ' + styles.sticky) : '')} ref="actionBar">
            <div className={styles.left}>
              <p className={styles.subtotal}>{shoppingCart.subtotal_text}</p>
              <p className={styles.itemCount}>{totalItemCount} item{totalItemCount !== 1 ? 's' : ''} in bag</p>
            </div>
            {checkoutButton}
            {mobileCheckoutButton}
          </div>
          <div className={styles.placeholderActionBar} />
        </div>
        {this.renderAlerts(shoppingCart)}
        <div className={styles.itemsList}>
          {this.renderItems(shoppingCart)}
        </div>
        <div className={styles.postTableActions}>
          <div className={styles.rightColumn}>
            <div className={styles.couponCodeForm}>
              {couponCodeForm}
            </div>
            <div className={styles.subtotalRow}>
              <span className={styles.label}>Subtotal</span>
              <span className={styles.value}>{shoppingCart.subtotal_text}</span>
            </div>
            {this.renderInvoice(shoppingCart.invoice)}
            <div className={styles.buttonsContainer}>
              {checkoutButton}
              {keepShoppingButton}
            </div>
            <div className={styles.weAcceptFooter}>
              <span className={styles.ccAmericanExpress} />
              <span className={styles.ccMasterCard} />
              <span className={styles.ccDiscover} />
              <span className={styles.ccVisa} />
              <span className={styles.weAcceptMessage}>We accept</span>
            </div>
          </div>
        </div>
        <SimilarProductsSection {...similarProductsSectionProps} />
      </div>
    );
  }
}

export default ShoppingCartContents;



// WEBPACK FOOTER //
// banquet/components/ShoppingCartContents/index.js